import { anthropicStreamMessage, chatStreamCompletion } from "./language-input";
import { USAGE_QUERY_LIST_FILTERS, ADMIN_REFRESH_HEADER, normalizePublicErrorInput, pickPublicErrorFields, taskWithRequestDetails } from "@nexra/contracts";
import { getLocale } from "./i18n";
import type {
  AdminErrorOverview,
  FinanceChartsPage,
  FinanceTableExportInput,
  AdminUserApiKeyUpdate,
  AdminUserApiKeyUpdateResult,
  AdminUserDetail,
  AdminUserListPage,
  AdminUserListQuery,
  AdminUserPricingAdjustmentRow,
  AdminUserUpdate,
  AdminUserUpdateResult,
  AdminWalletRechargeInput,
  AdminWalletRechargeResult,
  CursorPage,
  JsonObject,
  JsonValue,
  ModelResourceList,
  ModelSchema,
  Task,
  Voice,
  VoiceCreation,
  CreateVoiceRequest,
  TaskResult,
  RequestDetails,
  TaskListParams,
  TaskRun,
  TaskRunListParams,
  TaskModel,
  TaskModelSummary,
  UsageAnomaliesPage,
  UsageAnomaliesQuery,
  UsageAnomalySummaryPage,
  UsageAnomalySummaryQuery,
  UsageDetailsPage,
  UsageDetailsQuery,
  UsageFilters,
  UsageOverviewPage,
  UsageFilterKind,
  UsageFilterOptionsPage,
  UsageFilterOptionsQuery,
  UserUsagePage,
  UserUsageWindow,
  UsageSummaryPage,
  UsageSummaryQuery,
  PublicError,
  PublicErrorDetails,
  PublicUpstreamError,
} from "@nexra/contracts";

function usageFiltersQuery(filters: UsageFilters): URLSearchParams {
  const query = new URLSearchParams({ start: filters.start, end: filters.end });
  if (filters.timeZoneOffsetMinutes !== undefined) {
    query.set("timeZoneOffsetMinutes", String(filters.timeZoneOffsetMinutes));
  }
  for (const key of USAGE_QUERY_LIST_FILTERS) {
    const list = filters[key];
    if (list?.length) query.set(key, list.join(","));
  }
  if (filters.upstreamModelId) query.set("upstreamModelId", filters.upstreamModelId);
  if (filters.userQuery) query.set("userQuery", filters.userQuery);
  if (filters.productIds?.length) query.set("productIds", filters.productIds.join(","));
  return query;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: PublicErrorDetails,
    readonly nexraRequestId?: string,
    readonly upstream?: PublicUpstreamError,
    readonly nexraHttpStatus?: number,
    readonly publicError?: PublicError,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** @deprecated Use nexraRequestId. */
  get requestId(): string | undefined {
    return this.nexraRequestId;
  }
}

type ApiErrorBody = {
  error?: unknown;
  code?: string;
  message?: string;
  details?: unknown;
  requestId?: string;
};

export function apiErrorFromResponse(status: number, body: unknown, requestId?: string): ApiError {
  const payload = body && typeof body === "object" && !Array.isArray(body) ? body as ApiErrorBody : {};
  const candidate = payload.error && typeof payload.error === "object" && !Array.isArray(payload.error) ? payload.error as ApiErrorBody : payload;
  const normalized = normalizePublicErrorInput(body, { nexraRequestId: requestId, nexraHttpStatus: status });
  const message = normalized?.nexraError.message ?? candidate.message;
  return new ApiError(
    normalized ? normalized.nexraError.message : typeof message === "string" && message.trim() ? message : `HTTP ${status}`,
    status,
    normalized?.nexraError.code ?? candidate.code,
    normalized?.nexraError.param ? { param: normalized.nexraError.param } : undefined,
    normalized?.nexraRequestId ?? requestId,
    undefined,
    normalized ? normalized.nexraHttpStatus : requestId ? status : undefined,
    normalized,
  );
}

export function apiRequestHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  headers.set("accept-language", getLocale());
  return headers;
}

declare global {
  // Injected by Portal /runtime-env.js in deployed environments.
  var __NEXRA_API_URL__: string | undefined;
}

export function browserApiUrl(path: string): string {
  const suffix = path.replace(/^\//, "");
  const origin = globalThis.__NEXRA_API_URL__?.trim().replace(/\/+$/, "");
  return origin ? `${origin}/${suffix}` : `/backend/${suffix}`;
}

async function downloadApiFile(path: string, fallbackFilename: string, body?: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(browserApiUrl(path), {
    method: "POST",
    cache: "no-store",
    credentials: "include",
    headers: apiRequestHeaders(body ? { "content-type": "application/json" } : undefined),
    ...(body ? { body } : {}),
  });
  if (!response.ok) {
    throw apiErrorFromResponse(response.status, await response.json().catch(() => ({})), response.headers.get("x-request-id") ?? undefined);
  }
  const filename = /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1]
    ?? fallbackFilename;
  return { blob: await response.blob(), filename };
}

export async function api<T>(path: string, init: RequestInit & { refresh?: boolean } = {}): Promise<T> {
  const { refresh, ...requestInit } = init;
  const headers = apiRequestHeaders(requestInit.headers);
  if (refresh) headers.set(ADMIN_REFRESH_HEADER, "1");
  if (requestInit.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const response = await fetch(browserApiUrl(path), {
    ...requestInit,
    cache: "no-store",
    credentials: "include",
    headers,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw apiErrorFromResponse(response.status, body, response.headers.get("x-request-id") ?? undefined);
  }
  return body as T;
}

export type SessionData = Readonly<{
  session: { id: string; expiresAt: string } | null;
  user: { id: string; name: string; email: string; image?: string | null } | null;
}>;

export type UserRequest = Readonly<RequestDetails>;

export function normalizeRequestDetails(value: RequestDetails): RequestDetails {
  const legacy = value as RequestDetails & { requestId?: string; httpStatus?: number | null; error?: unknown };
  const { requestId, httpStatus, error: _error, ...details } = legacy;
  return { ...details, ...pickPublicErrorFields(value, { nexraRequestId: requestId, nexraHttpStatus: httpStatus ?? undefined }) };
}


export type UserRequestFilters = import("@nexra/contracts").RequestHistoryFilters & Readonly<{
  scope?: "system";
  model?: string;
  status?: string;
  query?: string;
  limit?: number;
  cursor?: string;
  includeAssets?: boolean;
}>;

export type LanguageToolExecution = Readonly<{ id?: string; name: string; arguments: JsonObject; result: JsonObject }>;

export type LanguageSandboxResult = Readonly<{
  model: string;
  protocol: string;
  output: Readonly<{ text: string; reasoning?: string; tool_use?: JsonObject[] }>;
  usage: JsonObject;
  raw: JsonObject;
  demo?: Readonly<{
    mode: "sse" | "reasoning" | "tool_call";
    reasoning?: JsonObject;
    stream?: JsonObject;
    tool?: LanguageToolExecution;
    tools?: readonly LanguageToolExecution[];
  }>;
}>;

export type LanguageMessage = { role: "user" | "assistant"; content: string };

export type LanguageSandboxMode = "text" | "sse" | "reasoning" | "tool_call";

export type UserApiKey = Readonly<{
  id: string;
  name: string;
  keyHint: string;
  scopes: string[];
  status: "active" | "expired" | "revoked";
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}>;

// 钱包视图层类型（GET /v1/wallets、GET /v1/wallets/transactions 响应形状）。
export type UserWallet = Readonly<{
  id: string;
  currency: string;
  status: "active" | "frozen" | "closed";
  balance: string;
  reservedBalance: string;
  availableBalance: string;
  createdAt: string;
}>;

export type WalletEntry = Readonly<{
  id: string;
  walletId: string;
  currency: string;
  entryType: "opening_balance" | "recharge" | "charge" | "refund" | "adjustment";
  amount: string;
  balanceAfter: string;
  referenceType: string;
  referenceId: string;
  occurredAt: string;
  modelName: string | null;
  modelPublicId: string | null;
  operation: string | null;
  requestId: string | null;
}>;

export type AdminUserSummary = Readonly<{
  id: string;
  email: string;
  name: string;
  createdAt: string;
}>;

export type AdminModel = Readonly<{
  id: string;
  slug: string;
  displayName: string;
  summary: string | null;
  modelKey: string;
  publicId: string;
  publicAliases: string[];
  contractVersion: number;
  developerSlug: string | null;
  developerName: string | null;
  family: string | null;
  kind: string;
  operation: string;
  category: string;
  capabilities: Record<string, unknown>;
  tags: string[];
  status: string;
  visibility: string;
  sourceKind: "code" | "legacy";
  endpointLabels: string[];
  version: number;
  endpointCount: number;
  parameterizedEndpointCount: number;
  executableEndpointCount: number;
  channelCount: number;
  channelModelCount: number;
  officialPriceCount: number;
  costedChannelModelCount: number;
  upstreamChannels: ReadonlyArray<Readonly<{
    providerKey: string;
    providerName: string;
    channelName: string;
    status: string;
  }>>;
  publishedAt: string | null;
  updatedAt: string;
}>;

export type AdminModelEndpoint = Readonly<{
  origin?: "model" | "channel" | "platform" | "bridge";
  declaredInModel?: boolean;
  id: string;
  /** Null for a code declaration without a persisted runtime endpoint. */
  runtimeEndpointId?: string | null;
  key: string;
  label: string;
  operation: string;
  protocol: string;
  method: string;
  path: string;
  parameterSchema: JsonObject;
  sourceEndpointKey?: string | null;
  documentationUrl?: string | null;
  httpOperations?: ReadonlyArray<Readonly<{
    key: string;
    kind: "request" | "create" | "query" | "cancel" | "delete" | "stream" | "generate" | "edit";
    httpMethod: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    pathTemplate: string;
    schemaProjection: "request" | "bearer-request" | "managed-task-create" | "managed-task-query" | "managed-task-cancel" | "native-task-create" | "native-task-query" | "native-task-delete";
    responseStatus?: number;
    idempotencyKey?: boolean;
    requestBody?: Readonly<{
      properties: readonly string[];
      required: readonly string[];
      oneOfRequired?: readonly (readonly string[])[];
    }>;
  }>>;
  schemaVersion: number;
  capabilities: string[];
  status: string;
  implementations: AdminEndpointImplementation[];
}>;

export type AdminEndpointImplementation = Readonly<{
  id: string;
  endpointId: string;
  endpointKey: string;
  endpointName: string;
  operation: string;
  method: string;
  path: string;
  strategy: string;
  publicProtocol: string;
  upstreamProtocol: string;
  upstreamOperation: string;
  executionMode: string;
  handlerKey: string;
  handlerVersion: string;
  handlerHash: string;
  handlerConfig: JsonObject;
  providerKeys: string[];
}>;

export type AdminChannelRoute = Readonly<{
  upstreamPath?: string | null;
  id: string;
  implementationId: string;
  endpointId: string;
  endpointKey: string;
  endpointName: string;
  channelModelId: string;
  strategy: string;
  status: string;
  priority: number;
  weight: number;
  channelStatus: string;
  channelModelStatus: string;
}>;

export type AdminChannelModel = Readonly<{
  id: string;
  providerKey: string;
  providerName: string;
  channelId: string;
  channelName: string;
  channelStatus: string;
  status: string;
  maxActiveJobs?: number | null;
  selected: boolean;
  sourceKind: string;
  matchKind: string;
  lastSyncedAt: string | null;
  upstreamModelId: string;
  routes: AdminChannelRoute[];
  prices: AdminModelPrice[];
}>;

export type AdminModelPrice = Readonly<{
  id: string;
  kind: "official" | "cost" | "sale";
  operation: string | null;
  channelModelId: string | null;
  channelId?: string | null;
  currency: string;
  status: "draft" | "published" | "retired";
  sourceKind: string;
  sourceRef: string | null;
  pricing: JsonObject;
  effectiveFrom: string;
  effectiveTo: string | null;
  publishedAt: string | null;
  createdAt: string;
  providerKey: string | null;
  channelName: string | null;
  /** 以下字段仅修订历史接口 GET /admin/models/:slug/prices 返回（TODO-14）。 */
  observedUpstreamModelId?: string | null;
  upstreamModelIdOverride?: string | null;
  createdByEmail?: string | null;
  publishedByEmail?: string | null;
}>;

export type AdminModelPriceReference = Pick<AdminModel, "id" | "slug" | "publicId" | "displayName" | "developerSlug" | "developerName"> & Readonly<{ prices: AdminModelPrice[] }>;

export type AdminUserModelDiscount = Readonly<{
  id: string;
  userId: string;
  userEmail: string;
  operation: string;
  discountBps: number;
  status: "active" | "retired";
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
  createdAt: string;
}>;

export type AdminModelDetail = AdminModel & Readonly<{
  channelProtocolOptions?: { providerKey: string; protocol: string; label: string; path: string; upstreamPath: string }[];
  description: string | null;
  versionLabel: string | null;
  inputModalities: string[];
  outputModalities: string[];
  featured: boolean;
  legacyIds: string[];
  operations: Record<string, unknown>[];
  schema: Record<string, unknown>;
  sourceKind: "code" | "legacy";
  sourceRef: string | null;
  manualFields: string[];
  bindingCount: number;
  endpoints: AdminModelEndpoint[];
  channelModels: AdminChannelModel[];
  prices: AdminModelPrice[];
  discounts: AdminUserModelDiscount[];
}>;

export type CatalogReviewSection = "identity" | "schema" | "channel" | "price";
export type CatalogReviewStatus = "pending" | "verified" | "needs_correction";

export type CatalogReviewDecision = Readonly<{
  status: CatalogReviewStatus;
  note: string | null;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
}>;

export type CatalogModelReview = Readonly<{
  version: 1;
  overallStatus: CatalogReviewStatus;
  sections: Readonly<Record<CatalogReviewSection, CatalogReviewDecision>>;
}>;

export type CatalogValidationProjection = Readonly<{
  sourceProvider: string;
  model: string;
  modelAlias: string;
  modelKey: string;
  developerKey: string;
  kind: string;
  operation: string;
  source: string;
  identity: Readonly<{ status: string; warnings: string[] }>;
  schema: Readonly<{
    status: string;
    parameterCount: number;
    requiredParameters: string[];
    defaultedParameters: string[];
    responseFields: string[];
    errors: string[];
    warnings: string[];
  }>;
  parameterReview: Readonly<{
    status: string;
    reviewedAt: string | null;
    sourceRefs: string[];
    notes: string[];
  }>;
  channel: Readonly<{
    status: string;
    providerKeys: string[];
    upstreamCandidates: string[];
    invocationCandidates: string[];
  }>;
  price: Readonly<{
    status: string;
    evidenceCount: number;
    officialPublishedPrices: number;
    officialDraftPrices: number;
    salePublishedPrices: number;
    saleDraftPrices: number;
    costPublishedPrices: number;
    costDraftPrices: number;
  }>;
  smoke: Readonly<{
    status: string;
    invocationModel: string | null;
    httpStatus: number | null;
    latencyMs: number | null;
    usage: JsonObject;
  }>;
  readiness: string;
  reviewNotes: string[];
}>;

export type AdminCatalogReviewModel = Readonly<{
  id: string;
  slug: string;
  displayName: string;
  kind: string;
  operation: string;
  lifecycleStatus: string;
  review: CatalogModelReview;
  projections: CatalogValidationProjection[];
}>;

export type AdminCatalogReviewQueue = Readonly<{
  generatedAt: string;
  summary: Readonly<{
    total: number;
    uniqueModels: number;
    channelVerified: number;
    configuredUnverified: number;
    noChannel: number;
    schemaInvalid: number;
    schemaReviewRequired: number;
    parametersVerified: number;
    parametersMissing: number;
    formalPrice: number;
    draftPrice: number;
    partialPrice: number;
    priceEvidence: number;
    priceMissing: number;
    catalogModels: number;
    modelsWithSourceEvidence: number;
    reviewPending: number;
    reviewVerified: number;
    reviewNeedsCorrection: number;
  }>;
  data: AdminCatalogReviewModel[];
}>;

export type AdminTask = Readonly<{
  id: string;
  userId: string;
  userEmail: string;
  model: string;
  operation: string;
  status: string;
  progress: number;
  currentAttempt: number;
  error: unknown;
  callbackStatus: string | null;
  callbackAttemptCount: number | null;
  callbackDeliveryGeneration: number | null;
  callbackLastHttpStatus: number | null;
  callbackLastAttemptAt: string | null;
  callbackNextAttemptAt: string | null;
  callbackDeliveredAt: string | null;
  callbackError: unknown;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}>;

export type AdminTaskDetail = Readonly<AdminTask & {
  modelVersion: number;
  publicProtocol: string;
  requestId: string | null;
  runId: string | null;
  runPosition: number | null;
  restateInvocationId: string | null;
  input: JsonObject;
  result: JsonObject | null;
  callbackUrl: string | null;
  callbackEvents: string[] | null;
  callbackDispatchedGeneration: number | null;
  callbackLastDispatchedAt: string | null;
  cancellationRequestedAt: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  attempts: ReadonlyArray<Readonly<{
    attempt: number;
    providerKey: string;
    channelName: string;
    routeId: string;
    upstreamProtocol: string;
    upstreamModelId: string;
    handlerKey: string;
    handlerVersion: string;
    handlerHash: string;
    upstreamTaskId: string | null;
    status: string;
    pollContext: JsonObject;
    error: unknown;
    nextPollAt: string | null;
    startedAt: string;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>>;
  events: ReadonlyArray<Readonly<{
    sequence: number;
    attempt: number | null;
    phase: string;
    level: string;
    type: string;
    fromStatus: string | null;
    toStatus: string | null;
    errorCode: string | null;
    errorCategory: string | null;
    message: string | null;
    payload: JsonObject;
    occurredAt: string;
  }>>;
  assets: ReadonlyArray<Readonly<{
    id: string;
    role: string;
    fieldPath: string;
    position: number;
    kind: string;
    source: string;
    status: string;
    name: string | null;
    mimeType: string | null;
    storageProvider: string | null;
    bucket: string | null;
    objectKey: string | null;
    externalUrl: string | null;
    checksumSha256: string | null;
    sizeBytes: number | null;
    width: number | null;
    height: number | null;
    durationMs: number | null;
    persisted: boolean;
    expiresAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>>;
}>;

export type AdminRequest = UserRequest & Readonly<{
  userId: string | null;
  userEmail: string | null;
  publicProtocol: string;
}>;

export type AdminChannel = Readonly<{
  configRevision?: string;
  health?: JsonObject;
  connectionConfig?: JsonObject;
  id: string;
  providerKey: string;
  name: string;
  baseUrl: string;
  region: string | null;
  authMethod: string;
  secretStorage: "database" | "none";
  secretHint: string | null;
  status: string;
  maxActiveJobs?: number | null;
  secretVersion: number;
  secretExpiresAt: string | null;
  secretRotatedAt: string | null;
  lastValidatedAt: string | null;
  lastHealthCheckAt: string | null;
  selectedModelCount?: number;
  discoveredModelCount?: number;
  mappedModelCount?: number;
  costedModelCount?: number;
  modelInventoryFetchedAt?: string | null;
}>;

export type AdminProvider = Readonly<{
  providerKey: string;
  displayName: string;
  authKind: string;
  secretFields: string[];
  optionalSecretFields?: string[];
  validationSupported: boolean;
  modelDiscoverySupported?: boolean;
}>;

export type ChannelModelInventory = Readonly<{
  models: readonly {
    id: string;
    name: string;
    upstream: boolean;
    configured: boolean;
    exactMatch: {
      modelKey: string;
      displayName: string;
      matchKind: "provider_alias" | "model_id";
    } | null;
    suggestions: readonly {
      modelKey: string;
      displayName: string;
      score: number;
    }[];
  }[];
  mappings: readonly Readonly<{
    channelModelId: string;
    upstreamModelId: string;
    publicModelId: string;
    modelSlug: string;
    displayName: string;
    status: string;
    channelStatus: string;
    sourceKind: string;
    matchKind: string;
    costConfigured: boolean;
    protocols: string[];
  }>[];
  selectedModelIds: readonly string[];
  selectionBaseline: readonly string[] | null;
  fetchedAt: string | null;
  partial: boolean;
}>;

export type AdminChannelValidation = Readonly<{
  channelId: string;
  providerKey: string;
  checkedAt: string;
  status: string;
  valid: boolean;
  code: string;
  message: string;
  httpStatus: number | null;
  latencyMs: number;
  upstreamRequestId: string | null;
  details: Readonly<Record<string, boolean | number | string | null>>;
}>;

export type AdminOperations = Readonly<{
  snapshot: Readonly<{
    databaseOk: boolean;
    gatewayRequests5m: number;
    gatewayErrors5m: number;
    gatewayFailures5m: number;
    gatewayStale: number;
    tasksQueued: number;
    tasksQueuedStale: number;
    tasksRunning: number;
    tasksFailed5m: number;
    tasksStale: number;
    tasksPollUnavailable: number;
    callbacksPending: number;
    callbacksRetrying: number;
    callbacksFailed: number;
    callbacksStale: number;
    callbacksUndispatched: number;
    providerMedia: number;
    providerMediaUnpersisted: number;
    providerMediaFailed: number;
    inputMediaUnpersisted: number;
    inputMediaFailed: number;
    billingHoldsExpired: number;
    billingChargesPendingStale: number;
    billingChargesFailed5m: number;
    channelsActive: number;
    channelsEnabled: number;
    channelsDegraded: number;
    channelsDisabled: number;
  }>;
  mediaStorageConfigured: boolean;
}>;

export type AdminGatewayRequestRow = Readonly<{
  id: string;
  requestId: string;
  accessMode: string | null;
  sessionId?: string | null;
  taskId: string | null;
  taskStatus: string | null;
  persistPolicy?: "terminal" | "background" | null;
  deliveryStatus?: "preparing" | "ready" | "failed" | null;
  userId: string | null;
  userEmail: string | null;
  callerUrl?: string | null;
  endUserId?: string | null;
  clientIp?: string | null;
  endUserIp?: string | null;
  purpose?: string | null;
  sourceSessionId?: string | null;
  sourceTaskId?: string | null;
  method: string;
  path: string;
  publicProtocol: string;
  model: string | null;
  operation: string | null;
  status: string;
  httpStatus: number | null;
  errorCode: string | null;
  durationMs: number | null;
  taskDurationMs?: number | null;
  taskSubmittedAt?: string | null;
  taskCreatedAt?: string | null;
  observedAt?: string;
  storageDisposition: "stored" | "pending" | "failed" | "forwarded" | "none";
  mediaAssetCount: number;
  receivedAt: string;
  finishedAt: string | null;
}>;

export type AdminGatewayRequestQuery = Readonly<{
  id?: string;
  kind?: "task" | "direct";
  start?: string;
  end?: string;
  userId?: string;
  modelId?: string;
  operation?: string;
  protocol?: string;
  status?: string;
  apiKeyId?: string;
  apiKeyIds?: string;
  channelId?: string;
  providerId?: string;
  errorsOnly?: boolean;
  errorCode?: string;
  missingErrorCode?: boolean;
  limit: number;
  cursor?: string;
}>;

const ADMIN_REQUEST_QUERY_FIELDS = [
  "id", "kind", "start", "end", "userId", "modelId", "operation", "protocol", "status", "apiKeyId",
  "apiKeyIds", "channelId", "providerId", "errorCode", "errorsOnly", "missingErrorCode", "cursor",
] as const satisfies readonly (keyof AdminGatewayRequestQuery)[];

export type AdminGatewayRequestPage = Readonly<{
  data: AdminGatewayRequestRow[];
  nextCursor: string | null;
}>;

export type AdminStorageRelayItem = Readonly<{
  id: string;
  itemType: "asset" | "request";
  occurredAt: string;
  disposition: "stored" | "pending" | "failed" | "forwarded";
  surface: "task" | "proxy" | "upload" | "imported" | "other";
  role: "input" | "output" | null;
  kind: string | null;
  source: string | null;
  status: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  storageProvider: string | null;
  bucket: string | null;
  objectKey: string | null;
  externalUrl: string | null;
  persistenceError: string | null;
  refId: string | null;
  model: string | null;
  isStream: boolean | null;
  requestPath: string | null;
  httpStatus: number | null;
}>;

export type AdminStorageRelayOverview = Readonly<{
  hours: number;
  since: string;
  storedAssets: number;
  storedBytes: number;
  pendingAssets: number;
  failedAssets: number;
  forwardedProxyRequests: number;
  languageRequests: number;
  storage: Readonly<{
    configured: boolean;
    storageProvider: string;
    bucket: string | null;
    region: string | null;
    publicBaseUrl: string | null;
    maxBytes: number;
  }>;
}>;

export type AdminStorageRelayPage = Readonly<{
  overview: AdminStorageRelayOverview;
  data: AdminStorageRelayItem[];
  nextCursor: string | null;
}>;

export type AdminStorageRelayQuery = Readonly<{
  limit: number;
  disposition?: string;
  kind?: string;
  hours?: number;
  cursor?: string;
}>;

export type AdminGatewayRequestDetail = Readonly<{
  id: string;
  requestId: string;
  userId: string | null;
  userEmail: string | null;
  apiKeyId: string | null;
  apiKeyName?: string | null;
  /** Caller-asserted attribution; diagnostics display only. */
  callerUrl?: string | null;
  endUserId?: string | null;
  clientIp?: string | null;
  endUserIp?: string | null;
  purpose?: string | null;
  sourceSessionId?: string | null;
  sourceTaskId?: string | null;
  method: string;
  path: string;
  publicProtocol: string;
  accessMode: string | null;
  taskId: string | null;
  model: string | null;
  modelId: string | null;
  modelVersion: number | null;
  operation: string | null;
  status: string;
  httpStatus: number | null;
  isStream: boolean;
  traceId: string | null;
  errorCode: string | null;
  errorCategory: string | null;
  receivedAt: string;
  responseStartedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  taskDurationMs?: number | null;
  taskStatus?: string | null;
  persistPolicy?: "terminal" | "background" | null;
  deliveryStatus?: "preparing" | "ready" | "failed" | null;
  taskSubmittedAt?: string | null;
  taskCreatedAt?: string | null;
  observedAt?: string;
  attempts: ReadonlyArray<Readonly<{
    attempt: number;
    strategy: string;
    handlerKey: string;
    handlerVersion: string;
    upstreamProtocol: string;
    upstreamModelId: string | null;
    status: string;
    httpStatus: number | null;
    error: JsonObject | null;
    providerKey: string | null;
    channelName: string | null;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
  }>>;
  usage: ReadonlyArray<Readonly<{
    id: string;
    source: string;
    rawUsage: JsonObject;
    normalizedUsage: JsonObject;
    occurredAt: string;
  }>>;
  charges: ReadonlyArray<Readonly<{
    id: string;
    status: string;
    operation: string;
    costAmount: string | null;
    costCurrency: string | null;
    saleAmount: string | null;
    saleCurrency: string | null;
    finalizedAt: string | null;
    createdAt: string;
  }>>;
  mediaAssets: ReadonlyArray<Readonly<{ role: string; count: number }>>;
}>;

export type SaveProviderChannelInput = Readonly<{
  id?: string;
  selectedModelIds?: readonly string[];
  expectedSelectedModelIds?: readonly string[] | null;
  expectedConfigRevision?: string;
  status?: "active" | "disabled";
  maxActiveJobs?: number | null;
  providerKey: string;
  name: string;
  baseUrl: string;
  region?: string | null;
  authConfig?: JsonObject;
  secret?: Readonly<Record<string, string>>;
}>;

async function streamLanguage(
  model: string,
  prompt: string,
  requestedProtocol?: string,
  onDelta?: (text: string, result?: LanguageSandboxResult) => void,
  inputOrHistory?: JsonObject | LanguageMessage[],
  maxTokens?: number,
  modes?: LanguageSandboxMode[],
  sessionId?: string,
): Promise<LanguageSandboxResult> {
  const input = Array.isArray(inputOrHistory) ? undefined : inputOrHistory;
  const history = Array.isArray(inputOrHistory) ? inputOrHistory : undefined;
  const response = await fetch(browserApiUrl("v1/sandbox/language"), {
    method: "POST",
    cache: "no-store",
    credentials: "include",
    headers: apiRequestHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ model, prompt, history, maxTokens, modes, mode: "sse", ...(requestedProtocol ? { protocol: requestedProtocol } : {}), ...(input ? { input } : {}), ...(sessionId ? { sessionId } : {}) }),
  });
  if (!response.ok) {
    throw apiErrorFromResponse(response.status, await response.json().catch(() => ({})), response.headers.get("x-request-id") ?? undefined);
  }
  // Some compatible endpoints ignore stream=true and return a complete JSON response.
  if (response.headers.get("content-type")?.includes("application/json")) {
    const payload = await response.json() as JsonObject;
    if (payload.output && typeof payload.output === "object" && !Array.isArray(payload.output) && typeof payload.output.text === "string") return payload as unknown as LanguageSandboxResult;
    const choices = payload.choices as { message?: { content?: string; reasoning_content?: string } }[] | undefined;
    const message = choices?.[0]?.message;
    const text = message?.content ?? (typeof payload.output_text === "string" ? payload.output_text : "");
    const reasoning = message?.reasoning_content;
    if (!text && !reasoning) throw new ApiError("模型未返回可展示的内容，请查看请求记录。", 502, "empty_language_output");
    onDelta?.(text);
    return { model, protocol: requestedProtocol ?? "native-json", output: { text }, usage: {}, raw: payload,
      ...(reasoning ? { demo: { mode: "reasoning" as const, reasoning: { summary: reasoning } } } : {}) };
  }
  const reader = response.body?.getReader();
  if (!reader) throw new ApiError("SSE response body is unavailable", 502, "sse_body_missing");
  const decoder = new TextDecoder();
  const events: JsonValue[] = [];
  const nativeMessage = anthropicStreamMessage();
  const nativeChat = chatStreamCompletion();
  let toolMessages: JsonValue[] | undefined;
  let pending = "";
  let text = "";
  let reasoning = "";
  let completedResult: LanguageSandboxResult | undefined;
  const executedTools: LanguageToolExecution[] = [];
  const toolDemo = () => executedTools.length ? { tool: executedTools[0], tools: [...executedTools] } : {};
  const toolCalls = new Map<number, { name: string; arguments: string }>();
  while (true) {
    const chunk = await reader.read();
    pending += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done });
    const lines = pending.split(/\r?\n/);
    pending = chunk.done ? "" : lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (!data || data === "[DONE]") continue;
      let event: unknown;
      try {
        event = JSON.parse(data) as unknown;
      } catch {
        continue;
      }
      const publicFailure = event && typeof event === "object" && "nexraError" in event
        ? normalizePublicErrorInput(event) : undefined;
      if (publicFailure) {
        await reader.cancel();
        throw apiErrorFromResponse(publicFailure.nexraHttpStatus ?? 502, publicFailure, response.headers.get("x-request-id") ?? undefined);
      }
      if (events.length < 200 && isJsonValue(event)) events.push(event);
      const sandboxResult = completedSandboxResult(event);
      if (sandboxResult) {
        completedResult = sandboxResult;
        text = sandboxResult.output.text;
        onDelta?.(text, sandboxResult);
        continue;
      }
      nativeMessage.push(event);
      nativeChat.push(event);
      if (event && typeof event === "object" && ("error" in event || ("type" in event && event.type === "response.failed"))) {
        await reader.cancel();
        const failure = event as { error?: { message?: string }; response?: { error?: { message?: string } } };
        throw new ApiError(failure.error?.message ?? failure.response?.error?.message ?? "流式推理失败", 502, "stream_failed");
      }
      if (event && typeof event === "object" && "type" in event && event.type === "nexra.tool.executed") {
        const tool = event as unknown as LanguageToolExecution & { messages?: JsonValue[] };
        if (tool.messages) toolMessages = tool.messages;
        executedTools.push({ ...(tool.id ? { id: tool.id } : {}), name: tool.name, arguments: tool.arguments, result: tool.result });
        onDelta?.(text, { model, protocol: requestedProtocol ?? "native-sse", output: { text }, usage: {}, raw: {}, demo: { mode: "sse", ...toolDemo(), stream: { status: "streaming" } } });
      }
      const chunkEvent = event as { choices?: { delta?: { reasoning_content?: string; reasoning?: string } }[]; type?: string; delta?: { thinking?: string } };
      const thought = chunkEvent?.choices?.[0]?.delta?.reasoning_content ?? chunkEvent?.choices?.[0]?.delta?.reasoning ?? (chunkEvent?.type === "content_block_delta" ? chunkEvent.delta?.thinking : undefined);
      if (typeof thought === "string") reasoning += thought;
      const calls = (event as { choices?: { delta?: { tool_calls?: { index?: number; function?: { name?: string; arguments?: string } }[] } }[] })?.choices?.[0]?.delta?.tool_calls ?? [];
      for (const call of calls) {
        const index = call.index ?? 0;
        const previous = toolCalls.get(index) ?? { name: "", arguments: "" };
        toolCalls.set(index, { name: previous.name + (call.function?.name ?? ""), arguments: previous.arguments + (call.function?.arguments ?? "") });
      }
      const delta = languageSseDelta(event);
      if (delta) {
        text += delta;
      }
      if (delta || calls.length || thought) onDelta?.(text, {
        model, protocol: requestedProtocol ?? "native-sse", output: { text, tool_use: [...toolCalls.values()].map(call => ({ name: call.name, arguments: call.arguments })) }, usage: {}, raw: {},
        demo: { mode: "sse", ...toolDemo(), stream: { status: "streaming" }, ...(reasoning ? { reasoning: { summary: reasoning } } : {}) },
      });
    }
    if (chunk.done) break;
  }
  if (completedResult) return completedResult;
  if (nativeMessage.message && !nativeMessage.complete) throw new ApiError("上游流在 message_stop 前中断，请查看请求记录。", 502, "incomplete_language_stream");
  if (nativeChat.completion && !nativeChat.complete) throw new ApiError("上游流在回答结束前中断，请查看请求记录。", 502, "incomplete_language_stream");
  if (nativeMessage.message?.stop_reason === "refusal") text = "";
  for (const [index, value] of (Array.isArray(nativeMessage.message?.content) ? nativeMessage.message.content : []).entries()) {
    if (value && typeof value === "object" && !Array.isArray(value) && value.type === "tool_use") toolCalls.set(index, { name: String(value.name), arguments: JSON.stringify(value.input) });
  }
  if (!text && !reasoning && !toolCalls.size && !executedTools.length && nativeMessage.message?.stop_reason !== "refusal") throw new ApiError("模型流已结束，但未返回可展示的文本或推理内容。", 502, "empty_language_output");
  const protocol = response.headers.get("x-nexra-language-protocol") ?? requestedProtocol ?? "native-sse";
  return {
    model,
    protocol,
    output: { text, tool_use: [...toolCalls.values()].map(call => ({ name: call.name, arguments: call.arguments })) },
    usage: (nativeChat.completion?.usage as JsonObject | undefined) ?? {},
    raw: { events, ...nativeChat.completion, ...(nativeMessage.message ? { message: nativeMessage.message } : {}), ...(toolMessages ? { messages: toolMessages } : {}) },
    demo: { mode: "sse", ...toolDemo(), stream: { events: events.length }, ...(reasoning ? { reasoning: { summary: reasoning } } : {}) },
  };
}

function completedSandboxResult(value: unknown): LanguageSandboxResult | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const event = value as Record<string, unknown>;
  if (event.type !== "nexra.sandbox.result" || !event.result || typeof event.result !== "object" || Array.isArray(event.result)) return undefined;
  const result = event.result as Record<string, unknown>;
  const output = result.output;
  if (typeof result.model !== "string" || typeof result.protocol !== "string" || !output || typeof output !== "object" || Array.isArray(output)) return undefined;
  if (typeof (output as Record<string, unknown>).text !== "string") return undefined;
  return event.result as LanguageSandboxResult;
}

function languageSseDelta(value: unknown): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const event = value as Record<string, unknown>;
  if (event.type === "response.output_text.delta" && typeof event.delta === "string") {
    return event.delta;
  }
  if (event.type === "content_block_delta") {
    const delta = event.delta;
    if (delta && typeof delta === "object" && !Array.isArray(delta)) {
      const text = (delta as Record<string, unknown>).text;
      return typeof text === "string" ? text : "";
    }
  }
  const candidate = Array.isArray(event.candidates) ? event.candidates[0] : undefined;
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    const content = (candidate as Record<string, unknown>).content;
    if (content && typeof content === "object" && !Array.isArray(content)) {
      const parts = (content as Record<string, unknown>).parts;
      return (Array.isArray(parts) ? parts : []).map((part: unknown) => {
        if (!part || typeof part !== "object" || Array.isArray(part)) return "";
        const value = part as Record<string, unknown>;
        return value.thought !== true && typeof value.text === "string" ? value.text : "";
      }).join("");
    }
  }
  const choices = Array.isArray(event.choices) ? event.choices : [];
  const first = choices[0];
  if (first && typeof first === "object" && !Array.isArray(first)) {
    const delta = (first as Record<string, unknown>).delta;
    if (delta && typeof delta === "object" && !Array.isArray(delta)) {
      const content = (delta as Record<string, unknown>).content;
      return typeof content === "string" ? content : "";
    }
  }
  return "";
}

function isJsonValue(value: unknown): value is JsonValue {
  if (value === null || ["string", "number", "boolean"].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  return Boolean(value) && typeof value === "object"
    && Object.values(value as Record<string, unknown>).every(isJsonValue);
}

// ---- Admin usage export jobs (TODO-16D) ----

export type UsageExportFormat = "xlsx" | "csv";
export type UsageExportStatus = "queued" | "processing" | "succeeded" | "failed" | "expired";

/** 与 @nexra/db 的 UsageExportJob 对齐；contracts 暂无该类型，先手写。 */
export type UsageExportJob = Readonly<{
  id: string;
  idempotencyKey: string;
  requestedByUserId: string | null;
  format: UsageExportFormat;
  filters: JsonObject;
  status: UsageExportStatus;
  rowCount: number | null;
  fileKey: string | null;
  dataAsOf: string | null;
  error: JsonObject | null;
  createdAt: string;
  updatedAt: string;
}>;

function unwrapUsageExportJob(data: { job?: UsageExportJob } | UsageExportJob): UsageExportJob {
  if ("job" in data && data.job) return data.job;
  // 后端返回裸 job 对象（无 "job" 包装键）时按 job 信任其字段。
  return data as UsageExportJob;
}

// ---- Admin wallet list (admin-console-status B2) ----
// 与 @nexra/db 的 AdminWalletRow / AdminWalletSummaryRow 对齐；contracts 暂无该类型，先手写。

export type AdminWalletRow = Readonly<{
  walletId: string;
  userId: string;
  email: string;
  name: string;
  role: string;
  currency: string;
  /** DB numeric 文本，如 "50.00000000"。 */
  balance: string;
  reservedBalance: string;
  status: string;
  version: number;
  updatedAt: string;
}>;

export type AdminWalletListPage = Readonly<{
  data: AdminWalletRow[];
  nextCursor: string | null;
}>;

export type AdminWalletSummaryRow = Readonly<{
  currency: string;
  wallets: number;
  totalBalance: string;
  totalReservedBalance: string;
}>;

export type AdminWalletListParams = Readonly<{
  query?: string;
  currency?: string;
  status?: string;
  limit?: number;
  cursor?: string;
}>;

type PublicTaskRun = Omit<TaskRun, "tasks"> & { tasks: TaskResult[] };

// The console combines task results with platform request details. The public
// Task protocol itself does not carry billing, business tags or diagnostics.
export async function withTaskRequestDetails(tasks: readonly TaskResult[], signal?: AbortSignal): Promise<Task[]> {
  const ids = [...new Set(tasks.flatMap(task => task.nexraRequestId ? [task.nexraRequestId] : []))];
  const pages: UserRequest[] = [];
  await Promise.all(Array.from({ length: Math.ceil(ids.length / 100) }, async (_, index) => {
    const query = new URLSearchParams({ limit: "100", request_ids: ids.slice(index * 100, (index + 1) * 100).join(",") });
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const page = await api<CursorPage<UserRequest>>(`v1/requests?${query}`, { signal });
        pages.push(...page.data.map(normalizeRequestDetails));
        break;
      } catch (error) {
        signal?.throwIfAborted();
        if (attempt === 2 || (error instanceof ApiError && error.status !== 408 && error.status !== 429 && error.status < 500)) break;
        // Recover a failed auxiliary read even when a terminal task emits no more events.
        await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
        signal?.throwIfAborted();
      }
    }
  }));
  const requests = new Map(pages.map(request => [request.nexraRequestId, request]));
  return tasks.map(task => {
    const request = task.nexraRequestId ? requests.get(task.nexraRequestId) : undefined;
    const result = taskWithRequestDetails(task, request);
    return task.nexraRequestId && !request ? { ...result, billingStatus: "unavailable" as const } : result;
  });
}

async function withRunRequestDetails(runs: readonly PublicTaskRun[], signal?: AbortSignal): Promise<TaskRun[]> {
  const tasks = await withTaskRequestDetails(runs.flatMap(run => run.tasks), signal);
  const byId = new Map(tasks.map(task => [task.id, task]));
  return runs.map(run => ({ ...run, tasks: run.tasks.map(task => byId.get(task.id)!) }));
}

export const dataApi = {
  voices: async (signal?: AbortSignal) => (await api<{ data: Voice[] }>("v1/voices", { signal })).data,
  createVoice: (request: CreateVoiceRequest, idempotencyKey: string) => api<VoiceCreation>("v1/voices", {
    method: "POST", headers: { "idempotency-key": idempotencyKey }, body: JSON.stringify(request),
  }),
  session: (signal?: AbortSignal) => api<SessionData>("api/auth/get-session", { signal }),
  models: (query = "", signal?: AbortSignal) =>
    api<CursorPage<TaskModelSummary>>(`v1/models?limit=100${query ? `&q=${encodeURIComponent(query)}` : ""}`, { signal }),
  catalogModels: async (signal?: AbortSignal): Promise<CursorPage<TaskModelSummary>> => {
    const data: TaskModelSummary[] = [];
    const cursors = new Set<string>();
    let cursor: string | undefined;
    do {
      signal?.throwIfAborted();
      const page = await api<CursorPage<TaskModelSummary>>(`v1/models?limit=100&view=catalog${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`, { signal });
      data.push(...page.data);
      cursor = page.nextCursor;
      if (cursor && cursors.has(cursor)) throw new Error("Model catalog pagination did not advance");
      if (cursor) cursors.add(cursor);
    } while (cursor);
    return { data };
  },
  catalogModel: (slug: string, signal?: AbortSignal) => api<TaskModel>(`v1/models/${encodeURIComponent(slug)}?view=catalog`, { signal }),
  catalogModelSchema: (slug: string, endpoint?: string, signal?: AbortSignal) => api<ModelSchema>(`v1/models/${encodeURIComponent(slug)}/schema?view=catalog&contract=nexra${endpoint ? `&endpoint=${encodeURIComponent(endpoint)}` : ""}`, { signal }),
  catalogNativeModelSchema: (slug: string, endpoint?: string, signal?: AbortSignal) => api<ModelSchema>(`v1/models/${encodeURIComponent(slug)}/schema?view=catalog&contract=native${endpoint ? `&endpoint=${encodeURIComponent(endpoint)}` : ""}`, { signal }),
  model: (slug: string, signal?: AbortSignal) => api<TaskModel>(`v1/models/${encodeURIComponent(slug)}`, { signal }),
  modelSchema: (slug: string, endpoint?: string, signal?: AbortSignal) => api<ModelSchema>(`v1/models/${encodeURIComponent(slug)}/schema${endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ""}`, { signal }),
  modelResources: (slug: string, resource: string, signal?: AbortSignal) =>
    api<ModelResourceList>(`v1/models/${encodeURIComponent(slug)}/resources/${encodeURIComponent(resource)}`, { signal }),
  runLanguage: (model: string, prompt: string, mode: LanguageSandboxMode = "text", protocol?: string, inputOrHistory?: JsonObject | LanguageMessage[], maxTokens?: number, modes?: LanguageSandboxMode[], sessionId?: string) =>
    api<LanguageSandboxResult>("v1/sandbox/language", {
      method: "POST",
      body: JSON.stringify({ model, prompt, mode, maxTokens, modes, ...(protocol ? { protocol } : {}), ...(Array.isArray(inputOrHistory) ? { history: inputOrHistory } : inputOrHistory ? { input: inputOrHistory } : {}), ...(sessionId ? { sessionId } : {}) }),
    }),
  streamLanguage: streamLanguage,
  tasks: async (model?: string, params: TaskListParams = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams({ limit: String(params.limit ?? 100) });
    if (model ?? params.model) query.set("model", (model ?? params.model)!);
    if (params.status) query.set("status", params.status);
    appendHistoryFilters(query, params);
    if (params.orphanOnly) query.set("orphan_only", "1");
    if (params.cursor) query.set("cursor", params.cursor);
    const page = await api<CursorPage<TaskResult>>(`v1/tasks?${query}`, { signal });
    return { ...page, data: await withTaskRequestDetails(page.data, signal) };
  },
  task: async (taskId: string, signal?: AbortSignal) => (await withTaskRequestDetails([await api<TaskResult>(`v1/tasks/${encodeURIComponent(taskId)}`, { signal })], signal))[0]!,
  taskRuns: async (params: TaskRunListParams = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams({ limit: String(params.limit ?? 50) });
    if (params.model) query.set("model", params.model);
    if (params.status) query.set("status", params.status);
    if (params.cursor) query.set("cursor", params.cursor);
    const page = await api<CursorPage<PublicTaskRun>>(`v1/task-runs?${query}`, { signal });
    return { ...page, data: await withRunRequestDetails(page.data, signal) };
  },
  taskRun: async (runId: string, signal?: AbortSignal) => (await withRunRequestDetails([await api<PublicTaskRun>(`v1/task-runs/${encodeURIComponent(runId)}`, { signal })], signal))[0]!,
  createTaskRun: async (models: string[], repetitions: number, inputMode: string, input: JsonObject, submittedAt?: string, inputFormat?: "sandbox-v1") => {
    const run = await api<PublicTaskRun>("v1/task-runs", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ models, repetitions, inputMode, input, submittedAt, inputFormat }),
    });
    // Do not wait for diagnostics before displaying accepted cards.
    return { ...run, tasks: run.tasks.map(task => ({ ...task, input: run.input, assets: task.assets ?? [], operation: "", schemaVersion: 0, createdAt: run.createdAt, updatedAt: run.createdAt })) } satisfies TaskRun;
  },
  requests: (filters: UserRequestFilters = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams({ limit: String(filters.limit ?? 100) });
    if (filters.scope) query.set("scope", filters.scope);
    if (filters.model) query.set("model", filters.model);
    if (filters.status) query.set("status", filters.status);
    appendHistoryFilters(query, filters);
    if (filters.cursor) query.set("cursor", filters.cursor);
    if (filters.includeAssets) query.set("include_assets", "1");
    return api<CursorPage<UserRequest>>(`v1/requests?${query}`, { signal }).then(page => ({ ...page, data: page.data.map(normalizeRequestDetails) }));
  },
  request: (requestId: string, signal?: AbortSignal, scope?: "system") =>
    api<UserRequest>(`v1/requests/${encodeURIComponent(requestId)}${scope ? `?scope=${scope}` : ""}`, { signal }).then(normalizeRequestDetails),
  apiKeys: (params?: Readonly<{ limit?: number; cursor?: string }>, signal?: AbortSignal) => {
    const search = params ? new URLSearchParams({ limit: String(params.limit ?? 20) }) : undefined;
    if (params?.cursor) search!.set("cursor", params.cursor);
    return api<CursorPage<UserApiKey>>(`v1/api-keys${search ? `?${search}` : ""}`, { signal });
  },
  userUsage: (window: UserUsageWindow, signal?: AbortSignal) =>
    api<UserUsagePage>(`v1/account/usage?${usageFiltersQuery(window)}`, { signal, cache: "no-store" }),
  userWallets: (signal?: AbortSignal) => api<{ data: UserWallet[] }>("v1/wallets", { signal }),
  userWalletEntries: (params?: Readonly<{ limit?: number; cursor?: string }>, signal?: AbortSignal) => {
    const search = new URLSearchParams({ limit: String(params?.limit ?? 20) });
    if (params?.cursor) search.set("cursor", params.cursor);
    return api<CursorPage<WalletEntry>>(`v1/wallets/transactions?${search}`, { signal });
  },
  createApiKey: (input: { name: string; expiresInDays: number | null }) =>
    api<{ data: UserApiKey; secret: string }>("v1/api-keys", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  revokeApiKey: (keyId: string) =>
    api<{ data: { id: string; status: "revoked" } }>(`v1/api-keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE",
    }),
  adminModels: (params: Readonly<{ query?: string; kind?: string; lifecycle?: string; availability?: string; channelId?: string; developerSlug?: string; cursor?: string; limit?: number }> = {}, signal?: AbortSignal) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") search.set(key, String(value));
    return api<{ data: AdminModel[]; nextCursor: string | null }>(`admin/models?${search}`, { signal });
  },
  adminModel: (slug: string, signal?: AbortSignal) => api<AdminModelDetail>(`admin/models/${encodeURIComponent(slug)}`, { signal }),
  adminPricingPolicy: (signal?: AbortSignal) => api<{ saleMultiplier: string; usdCnyRate: string | null; billingCurrency: "CNY"; updatedAt: string | null }>("admin/pricing-policy", { signal }),
  publishAdminPricingPolicy: (saleMultiplier: string) => api<{ saleMultiplier: string; usdCnyRate: string | null; publishedSales: number }>("admin/pricing-policy", {
    method: "PUT",
    body: JSON.stringify({ saleMultiplier }),
  }),
  publishAdminPricingExchangeRate: (usdCnyRate: string) => api<{ saleMultiplier: string; usdCnyRate: string | null; publishedSales: number }>("admin/pricing-policy", {
    method: "PUT",
    body: JSON.stringify({ usdCnyRate }),
  }),
  updateAdminModel: (slug: string, input: Readonly<Record<string, unknown>>) =>
    api<{ model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateAdminModelSchema: (slug: string, operation: string, schema: JsonObject) =>
    api<{ contractVersion: number; model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/schema`, {
      method: "PATCH",
      body: JSON.stringify({ operation, schema }),
    }),
  updateAdminModelEndpoint: (slug: string, endpointId: string, input: Readonly<Record<string, unknown>>) =>
    api<{ model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/endpoints/${encodeURIComponent(endpointId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  saveAdminChannelModel: (slug: string, input: Readonly<Record<string, unknown>>) =>
    api<{ channelModelId: string; model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/channel-models`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  deleteAdminChannelModel: (slug: string, channelModelId: string) =>
    api<{ model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/channel-models/${encodeURIComponent(channelModelId)}`, {
      method: "DELETE",
    }),
  saveAdminOfficialPrice: (slug: string, input: Readonly<Record<string, unknown>>) =>
    api<{ price: { id: string }; model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/official-price`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  createAdminPrice: (slug: string, input: Readonly<Record<string, unknown>>) =>
    api<{ price: { id: string }; model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/prices`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  adminModelPrices: (slug: string, signal?: AbortSignal) =>
    api<{ prices: AdminModelPrice[] }>(`admin/models/${encodeURIComponent(slug)}/prices`, { signal }),
  adminModelPriceFilters: (signal?: AbortSignal) => api<{ manufacturers: Array<{ id: string; label: string }>; channels: Array<{ id: string; label: string }> }>("admin/model-prices/filters", { signal }),
  adminModelPriceList: (params: { query?: string; modelSlug?: string; developerSlug?: string; channelId?: string; cursor?: string; limit?: number } = {}, signal?: AbortSignal) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") search.set(key, String(value));
    return api<{ data: AdminModelPriceReference[]; nextCursor: string | null }>(`admin/model-prices?${search}`, { signal });
  },
  setAdminDiscount: (slug: string, input: Readonly<Record<string, unknown>>) =>
    api<{ discountId: string; model: AdminModelDetail }>(`admin/models/${encodeURIComponent(slug)}/discounts`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  setAdminPricingAdjustment: (
    input: Readonly<{
      subject: "user" | "api_key";
      userId?: string;
      apiKeyId?: string;
      scope: "global" | "model";
      modelSlug?: string;
      operation?: string;
      multiplier: string;
      effectiveFrom: string;
      effectiveTo: string | null;
      note: string | null;
      reason: string;
    }>,
  ) => api<{ adjustmentId: string }>("admin/pricing-adjustments", {
    method: "PUT",
    body: JSON.stringify(input),
  }),
  retireAdminPricingAdjustment: (id: string, reason: string) =>
    api<{ retired: boolean }>(`admin/pricing-adjustments/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    }),
  adminCatalogReview: () => api<AdminCatalogReviewQueue>("admin/catalog-review"),
  adminCatalogReviewModel: (slug: string) =>
    api<AdminCatalogReviewModel>(`admin/catalog-review/${encodeURIComponent(slug)}`),
  updateCatalogReview: (
    slug: string,
    input: Readonly<{ section: CatalogReviewSection; status: CatalogReviewStatus; note?: string | null }>,
  ) => api<{ review: CatalogModelReview }>(`admin/models/${encodeURIComponent(slug)}/review`, {
    method: "PATCH",
    body: JSON.stringify(input),
  }),
  saveProviderChannel: (input: SaveProviderChannelInput) =>
    api<{ channel: AdminChannel }>("admin/channels", {
      method: "PUT",
      body: JSON.stringify({
        ...input,
        reason: "Provider Channel configured in Admin Console",
      }),
    }),
  channelModels: (id: string, signal?: AbortSignal) => api<{ inventory: ChannelModelInventory }>(`admin/channels/${encodeURIComponent(id)}/models`, { signal }),
  refreshChannelModels: (id: string) => api<{ inventory: ChannelModelInventory }>(`admin/channels/${encodeURIComponent(id)}/models/refresh`, { method: "POST", body: "{}" }),
  importChannelModel: (id: string, input: Readonly<{ upstreamModelId: string; modelKey: string }>) =>
    api<{ inventory: ChannelModelInventory }>(`admin/channels/${encodeURIComponent(id)}/models/import`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  validateChannel: (channelId: string) =>
    api<{ validation: AdminChannelValidation }>(`admin/channels/${encodeURIComponent(channelId)}/validate`, {
      method: "POST",
      body: "{}",
    }),
  replayTaskCallback: (taskId: string) =>
    api<{ callback: { publicTaskId: string; deliveryGeneration: number } }>(
      `admin/tasks/${encodeURIComponent(taskId)}/callback/replay`,
      { method: "POST", body: "{}" },
    ),
  adminTask: (taskId: string, signal?: AbortSignal) =>
    api<AdminTaskDetail>(`admin/tasks/${encodeURIComponent(taskId)}`, { signal }),
  adminUsageSummary: (query: UsageSummaryQuery, signal?: AbortSignal) => {
    const params = usageFiltersQuery(query.filters);
    params.set("groupBy", query.groupBy.join(","));
    params.set("limit", String(query.limit));
    if (query.cursor) params.set("cursor", query.cursor);
    return api<UsageSummaryPage>(`admin/usage/summary?${params}`, { signal });
  },
  adminUsageFinance: (filters: UsageFilters, signal?: AbortSignal) =>
    api<FinanceChartsPage>(`admin/usage/finance?${usageFiltersQuery(filters)}`, { signal }),
  adminUsageOverview: (filters: UsageFilters, trendGroupBy: "hour" | "day" | "month", signal?: AbortSignal, refresh = false) => {
    const params = usageFiltersQuery(filters);
    params.set("trendGroupBy", trendGroupBy);
    return api<UsageOverviewPage>(`admin/usage/overview?${params}`, { signal, refresh });
  },
  adminUsageDetails: (query: UsageDetailsQuery, signal?: AbortSignal) => {
    const params = usageFiltersQuery(query.filters);
    params.set("limit", String(query.limit));
    if (query.cursor) params.set("cursor", query.cursor);
    return api<UsageDetailsPage>(`admin/usage/details?${params}`, { signal });
  },
  adminUsageAnomalies: (query: UsageAnomaliesQuery, signal?: AbortSignal) => {
    const params = usageFiltersQuery(query.filters);
    if (query.categories?.length) params.set("categories", query.categories.join(","));
    params.set("limit", String(query.limit));
    if (query.cursor) params.set("cursor", query.cursor);
    return api<UsageAnomaliesPage>(`admin/usage/anomalies?${params}`, { signal });
  },
  adminUsageAnomalySummary: (query: UsageAnomalySummaryQuery, signal?: AbortSignal) => {
    const params = usageFiltersQuery(query.filters);
    if (query.breakdowns?.length) params.set("breakdown", query.breakdowns.join(","));
    return api<UsageAnomalySummaryPage>(`admin/usage/anomalies/summary?${params}`, { signal });
  },
  createAdminUsageExport: (input: { filters: UsageFilters; format: UsageExportFormat; refreshOf?: string }) =>
    api<{ job: UsageExportJob }>("admin/usage/exports", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  listAdminUsageExports: async (query: { statuses?: UsageExportStatus[]; limit?: number } = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (query.statuses?.length) params.set("statuses", query.statuses.join(","));
    if (query.limit !== undefined) params.set("limit", String(query.limit));
    const suffix = params.size > 0 ? `?${params}` : "";
    const body = await api<{ data: UsageExportJob[] }>(`admin/usage/exports${suffix}`, { signal });
    if (!body || !Array.isArray(body.data)) {
      throw new Error("导出记录响应格式异常，请重试或联系管理员");
    }
    return body.data;
  },
  retryAdminUsageExport: (id: string) =>
    api<{ job: UsageExportJob }>(`admin/usage/exports/${encodeURIComponent(id)}/retry`, { method: "POST" }),
  deleteAdminUsageExport: (id: string) =>
    api<{ deleted: true }>(`admin/usage/exports/${encodeURIComponent(id)}`, { method: "DELETE" }),
  downloadAdminUsageExport: (id: string) => downloadApiFile(`admin/usage/exports/${encodeURIComponent(id)}/download`, `usage-${id}.xlsx`),
  exportAdminFinanceTable: (input: FinanceTableExportInput) => downloadApiFile("admin/usage/finance/export", `nexra-finance-${input.dimension}.xlsx`, JSON.stringify(input)),
  adminUsageExport: async (id: string) => {
    // 兼容两种响应结构：{ job } 或 job 对象。
    return unwrapUsageExportJob(await api<{ job?: UsageExportJob } | UsageExportJob>(
      `admin/usage/exports/${encodeURIComponent(id)}`,
    ));
  },
  adminErrorOverview: (filters: UsageFilters, signal?: AbortSignal) => {
    const params = new URLSearchParams({ start: filters.start, end: filters.end });
    for (const [key, param] of [["userIds", "userId"], ["modelIds", "modelId"], ["channelIds", "channelId"], ["providerIds", "providerId"], ["apiKeyIds", "apiKeyIds"], ["operations", "operation"]] as const) {
      if (filters[key]?.length) params.set(param, filters[key]!.join(","));
    }
    return api<AdminErrorOverview>(`admin/errors/overview?${params}`, { signal });
  },
  adminGatewayRequests: (query: AdminGatewayRequestQuery, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(query.limit), paged: "1" });
    for (const key of ADMIN_REQUEST_QUERY_FIELDS) {
      const value = query[key];
      if (value) params.set(key, typeof value === "boolean" ? "1" : value);
    }
    return api<AdminGatewayRequestPage>(`admin/requests?${params}`, { signal });
  },
  adminGatewayRequest: (id: string, signal?: AbortSignal) =>
    api<AdminGatewayRequestDetail>(`admin/requests/${encodeURIComponent(id)}`, { signal }),
  adminStorageRelay: (query: AdminStorageRelayQuery, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(query.limit) });
    if (query.disposition) params.set("disposition", query.disposition);
    if (query.kind) params.set("kind", query.kind);
    if (query.hours) params.set("hours", String(query.hours));
    if (query.cursor) params.set("cursor", query.cursor);
    return api<AdminStorageRelayPage>(`admin/storage-relay?${params}`, { signal });
  },
  adminUsers: (query: AdminUserListQuery, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(query.limit) });
    if (query.query) params.set("query", query.query);
    if (query.role) params.set("role", query.role);
    if (query.status) params.set("status", query.status);
    if (query.walletStatus) params.set("walletStatus", query.walletStatus);
    if (query.cursor) params.set("cursor", query.cursor);
    return api<AdminUserListPage>(`admin/users?${params}`, { signal });
  },
  adminUsageFilterOptions: (kind: UsageFilterKind, query: UsageFilterOptionsQuery, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(query.limit) });
    if (query.query) params.set("query", query.query);
    if (query.modelKind) params.set("modelKind", query.modelKind);
    if (query.cursor) params.set("cursor", query.cursor);
    if (query.ids) params.set("ids", query.ids.join(","));
    return api<UsageFilterOptionsPage>(`admin/usage/filter-options/${kind}?${params}`, { signal, cache: "no-store" });
  },
  openAdminUserWallet: (userId: string, input: import("@nexra/contracts").AdminUserWalletOpenInput) =>
    api<import("@nexra/contracts").AdminUserWalletOpenResult>(`admin/users/${encodeURIComponent(userId)}/wallets`, { method: "POST", body: JSON.stringify(input) }),
  adminUserActivity: (userId: string, query: { kind?: import("@nexra/contracts").AdminUserActivityKind; cursor?: string; limit: number }, signal?: AbortSignal) => {
    const params = new URLSearchParams({ limit: String(query.limit) });
    if (query.kind) params.set("kind", query.kind);
    if (query.cursor) params.set("cursor", query.cursor);
    return api<import("@nexra/contracts").AdminUserActivityPage>(`admin/users/${encodeURIComponent(userId)}/activity?${params}`, { signal });
  },
  adminUser: (userId: string, signal?: AbortSignal) =>
    api<AdminUserDetail>(`admin/users/${encodeURIComponent(userId)}`, { signal }),
  updateAdminUser: (userId: string, input: AdminUserUpdate) =>
    api<AdminUserUpdateResult>(`admin/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  adminDeleteUser: (userId: string, input: import("@nexra/contracts").AdminUserDeleteInput) =>
    api<import("@nexra/contracts").AdminUserDeleteResult>(`admin/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
      body: JSON.stringify(input),
    }),
  updateAdminUserApiKey: (userId: string, keyId: string, input: AdminUserApiKeyUpdate) =>
    api<AdminUserApiKeyUpdateResult>(`admin/users/${encodeURIComponent(userId)}/api-keys/${encodeURIComponent(keyId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  adminUserRecharge: (input: AdminWalletRechargeInput) =>
    api<AdminWalletRechargeResult>(`admin/users/${encodeURIComponent(input.userId)}/wallets/recharge`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  adminWallets: (params: AdminWalletListParams = {}, signal?: AbortSignal) => {
    const search = new URLSearchParams({ limit: String(params.limit ?? 50) });
    if (params.query) search.set("query", params.query);
    if (params.currency) search.set("currency", params.currency);
    if (params.status) search.set("status", params.status);
    if (params.cursor) search.set("cursor", params.cursor);
    return api<AdminWalletListPage>(`admin/wallets?${search}`, { signal });
  },
  adminWalletSummary: (signal?: AbortSignal) => api<AdminWalletSummaryRow[]>("admin/wallets/summary", { signal }),
  admin: async () => {
    const [models, tasks, requests, providers, channels, operations] = await Promise.all([
      api<{ data: AdminModel[] }>("admin/models?limit=200"),
      api<{ data: AdminTask[] }>("admin/tasks?limit=100"),
      api<{ data: AdminRequest[] }>("admin/requests?limit=100"),
      api<{ data: AdminProvider[] }>("admin/providers"),
      api<{ data: AdminChannel[] }>("admin/channels"),
      api<AdminOperations>("admin/operations"),
    ]);
    return {
      models: models.data,
      tasks: tasks.data,
      requests: requests.data,
      providers: providers.data,
      channels: channels.data,
      operations,
    };
  },
};

export type AdminData = Awaited<ReturnType<typeof dataApi.admin>>;

function appendHistoryFilters(search: URLSearchParams, filters: import("@nexra/contracts").RequestHistoryFilters) {
  if (filters.query?.trim()) search.set("q", filters.query.trim());
  if (filters.modality) search.set("modality", filters.modality);
  if (filters.kind) search.set("kind", filters.kind);
  if (filters.displayStatus) search.set("display_status", filters.displayStatus);
}
